import React, { useState, useRef, useEffect, useCallback } from "react";

export type SelectOption = {
  value: string;
  label: string;
};

type SelectProps = {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
  id: string;
  name?: string;
  hasError?: boolean;
  errorMessage?: string;
  dropUp?: boolean;
  onChange?: (value: string, name: string) => void;
  onBlur?: () => void;
};

const Select: React.FC<SelectProps> = ({
  options,
  value,
  placeholder,
  className,
  disabled,
  showLabel = true,
  label,
  id,
  name = id,
  hasError = false,
  errorMessage,
  dropUp = false,
  onChange,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue, name);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleClose = () => {
    setOpen(false);
    onBlur?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && options) {
      const selectedIdx = options.findIndex((opt) => opt.value === value);
      setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [open]);

  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const items = listRef.current.querySelectorAll("[role='option']");
      (items[focusedIndex] as HTMLElement)?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!options) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!open) {
            setOpen(true);
          } else {
            setFocusedIndex((prev) =>
              prev < options.length - 1 ? prev + 1 : prev
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (open) {
            setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          }
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (open && focusedIndex >= 0) {
            handleSelect(options[focusedIndex].value);
          } else if (!open) {
            setOpen(true);
          }
          break;
        case "Escape":
          e.preventDefault();
          handleClose();
          triggerRef.current?.focus();
          break;
        case "Home":
          if (open) {
            e.preventDefault();
            setFocusedIndex(0);
          }
          break;
        case "End":
          if (open) {
            e.preventDefault();
            setFocusedIndex(options.length - 1);
          }
          break;
      }
    },
    [open, focusedIndex, options]
  );

  const selectedOption = options?.find((opt) => opt.value === value);
  const listboxId = `${id}-listbox`;

  return (
    <div
      className={`flex flex-col gap-1 ${className ?? ""}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      {showLabel && label && (
        <label
          htmlFor={id}
          className="text-body-sm-emphasis text-[#303030]"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          ref={triggerRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          aria-invalid={hasError || undefined}
          aria-activedescendant={
            open && focusedIndex >= 0 && options?.[focusedIndex]
              ? `${id}-option-${focusedIndex}`
              : undefined
          }
          disabled={disabled}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          className={`flex w-full h-10 items-center justify-between rounded-md border px-3 text-sm font-normal leading-5 transition-colors ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-[#E8E8E8] text-[#6B6B6B]"
              : "bg-white cursor-pointer"
          } ${
            hasError
              ? "border-error ring-1 ring-error"
              : open
                ? "border-[#1e3a5f] ring-1 ring-[#1e3a5f]"
                : "border-[#E8E8E8]"
          }`}
        >
          <span
            className={`truncate ${
              selectedOption ? "text-[#303030]" : "text-[#A1A1AA]"
            }`}
          >
            {selectedOption?.label || placeholder}
          </span>
          <svg
            className={`w-4 h-4 shrink-0 text-[#6B6B6B] transition-transform ${
              open ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {open && (
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={label}
            className={`absolute z-10 w-full bg-white border border-[#E8E8E8] rounded-md shadow-lg max-h-60 overflow-y-auto text-sm ${
              dropUp ? "bottom-full mb-1" : "mt-1"
            }`}
          >
            {options?.map((option, index) => {
              const isSelected = option.value === value;
              const isFocused = index === focusedIndex;
              return (
                <div
                  key={option.value}
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`px-3 py-2 cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-[#2B8565] text-white"
                      : isFocused
                        ? "bg-[#F4F4F5] text-[#303030]"
                        : "text-[#303030] hover:bg-[#F4F4F5]"
                  }`}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {hasError && errorMessage && (
        <span id={`${id}-error`} className="text-xs text-red">{errorMessage}</span>
      )}
    </div>
  );
};

export default Select;
